// WF-CONTROL phase 1: profile edits (role, goal, backstory, standing orders, responsibilities)
// are recorded as revisions in CompanyOS. An agent's (AG) page shows "Not yet synced to runtime"
// while its newest revision is not synced (`runtime.unsynced`): nothing carries it to the live
// runtime yet. DE-PROFILE-LIVE (2026-10-30): a DE's profile is delivered at every run start
// (dispatch, MCP checkout / get_my_profile, the bridge's prompt), so its page says so instead.
import { Badge, InfoTip } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { agRevisionsQuery } from "./queries";

export function RuntimeSyncChip({
  api,
  co,
  subject,
}: {
  api: Api;
  co: string;
  subject: { kind: "de" | "ag"; id: string };
}) {
  const ag = useQuery({ ...agRevisionsQuery(api, co, subject.id), enabled: subject.kind === "ag" });
  if (subject.kind === "de")
    return (
      <span
        className="inline-flex items-center gap-1 self-start text-xs text-muted-foreground"
        data-slot="profile-delivered"
      >
        Delivered to the DE on its next run
        <InfoTip label="About profile delivery">
          Role, goal, backstory and standing orders go to the DE at the start of every run. Your
          edits are added on top of its shipped definition; where one conflicts, yours wins. An edit
          here reaches it on its next run.
        </InfoTip>
      </span>
    );
  if (!ag.data?.runtime.unsynced) return null;
  return (
    <span className="inline-flex items-center gap-1 self-start" data-slot="runtime-unsynced">
      <Badge variant="warning">Not yet synced to runtime</Badge>
      <InfoTip label="About runtime sync">
        Profile edits update CompanyOS records only. Pushing them to the live runtime comes in a
        later phase.
      </InfoTip>
    </span>
  );
}
